/**
 * `VirtualList` (W1-12 ui-kit-i18n; contract VirtualListProps): a virtualised listbox for long lists (lines,
 * problems, history, assets) with fixed row height. Only the rows in view (plus an overscan margin, plus the
 * active row) are in the DOM. One tab stop: the list itself, with aria-activedescendant pointing at the active
 * row. Keyboard: ArrowUp / ArrowDown, Home / End, PageUp / PageDown move the active row (scrolled into view);
 * Enter or Space activates it. A click makes a row active and activates it. `selected` drives aria-selected
 * (multi-selection lists); without it the active row is the selected one.
 */

import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { useT } from '../i18n/useT';
import { useTr } from './bridge';
import { cx } from './internal/cx';
import type { VirtualListProps } from './types';
import { m } from './ui.messages';
import s from './VirtualList.module.css';

const OVERSCAN = 6;
/** Rows assumed visible before the first measurement (and in environments without layout). */
const FALLBACK_ROWS = 30;

/** Visible index range [first, last) for a scroll position. */
export function visibleRange(
  scrollTop: number,
  viewport: number,
  rowHeight: number,
  count: number,
  overscan = OVERSCAN,
): readonly [first: number, last: number] {
  const h = Math.max(1, rowHeight);
  const first = Math.max(0, Math.floor(scrollTop / h) - overscan);
  const last = Math.min(count, Math.ceil((scrollTop + viewport) / h) + overscan);
  return [first, Math.max(first, last)];
}

export function VirtualList<T>(props: VirtualListProps<T> & { readonly className?: string | undefined }) {
  const t = useT();
  const tr = useTr();
  const baseId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(0);
  const [active, setActive] = useState(0);
  const items = props.items;
  const count = items.length;
  const h = Math.max(1, props.rowHeight);
  const activeIndex = count === 0 ? -1 : Math.min(Math.max(0, active), count - 1);
  const viewportPx = viewport > 0 ? viewport : FALLBACK_ROWS * h;

  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const observer = new ResizeObserver(() => {
      setViewport(el.clientHeight);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);

  const rowId = (i: number): string => `${baseId}-row-${String(i)}`;

  const reveal = (i: number): void => {
    const el = ref.current;
    if (el === null) return;
    const vh = el.clientHeight > 0 ? el.clientHeight : viewportPx;
    const top = i * h;
    if (top < el.scrollTop) el.scrollTop = top;
    else if (top + h > el.scrollTop + vh) el.scrollTop = top + h - vh;
    setScrollTop(el.scrollTop);
  };

  const activate = (i: number): void => {
    const item = items[i];
    if (item !== undefined) props.onActivate?.(item, i);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (count === 0 || e.altKey || e.ctrlKey || e.metaKey) return;
    const page = Math.max(1, Math.floor(viewportPx / h) - 1);
    let next: number;
    switch (e.key) {
      case 'ArrowDown':
        next = activeIndex + 1;
        break;
      case 'ArrowUp':
        next = activeIndex - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      case 'PageDown':
        next = activeIndex + page;
        break;
      case 'PageUp':
        next = activeIndex - page;
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        activate(activeIndex);
        return;
      default:
        return;
    }
    e.preventDefault();
    const clamped = Math.min(count - 1, Math.max(0, next));
    setActive(clamped);
    reveal(clamped);
  };

  const [first, last] = visibleRange(scrollTop, viewportPx, h, count);
  const indices: number[] = [];
  for (let i = first; i < last; i++) indices.push(i);
  if (activeIndex >= 0 && (activeIndex < first || activeIndex >= last)) indices.push(activeIndex);

  const rows: ReactNode[] = indices.map((i) => {
    const item = items[i] as T;
    const key = props.getKey(item);
    const isSelected = props.selected === undefined ? i === activeIndex : props.selected.has(key);
    return (
      <div
        key={key}
        id={rowId(i)}
        role="option"
        aria-selected={isSelected}
        aria-setsize={count}
        aria-posinset={i + 1}
        className={cx(s.row, i === activeIndex && s.active, isSelected && s.selected)}
        style={{ top: i * h, height: h }}
        onClick={(e) => {
          setActive(i);
          e.currentTarget.closest<HTMLElement>('[role="listbox"]')?.focus({ preventScroll: true });
          activate(i);
        }}
      >
        {props.render(item, i)}
      </div>
    );
  });

  return (
    <div className={cx(s.wrap, props.className)}>
      <div
        ref={ref}
        role="listbox"
        tabIndex={0}
        aria-label={tr(props.ariaLabel)}
        aria-multiselectable={props.selected === undefined ? undefined : true}
        aria-activedescendant={activeIndex >= 0 ? rowId(activeIndex) : undefined}
        className={s.list}
        onKeyDown={onKeyDown}
        onScroll={(e) => {
          setScrollTop(e.currentTarget.scrollTop);
        }}
      >
        <div className={s.spacer} style={{ height: count * h }}>
          {rows}
        </div>
      </div>
      {count === 0 && <p className={s.empty}>{t(m['list.empty'])}</p>}
    </div>
  );
}
